import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { ArrowRight, Home, Building2, Factory, Clock } from "lucide-react";
import { usageCategories } from "@/lib/usage-data";
import { blogPosts } from "@/lib/blog-data";

const accent = {
  residential: "from-amber-400/30 to-amber-200/10",
  commercial: "from-sky-400/30 to-sky-200/10",
  industrial: "from-slate-400/30 to-slate-200/10",
} as const;

const guideSlugByCategory: Record<string, string> = {
  residential: "residential-epoxy-flooring-nj",
  commercial: "commercial-epoxy-flooring-nj",
  industrial: "industrial-epoxy-flooring-nj",
};

export const Route = createFileRoute("/usage/")({
  head: () => ({
    meta: [
      { title: "Usage | Where Our Epoxy Floors Work, Blanco Pro Services" },
      { name: "description", content: "Residential, commercial, and industrial epoxy flooring applications across New Jersey, garages, kitchens, clinics, warehouses, labs, and more." },
      { property: "og:title", content: "Usage, Residential, Commercial & Industrial Epoxy" },
      { property: "og:description", content: "Explore every space we transform with high-performance epoxy and resinous flooring systems." },
      { property: "og:url", content: "https://blancopros.com/usage" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Usage, Residential, Commercial & Industrial Epoxy" },
      { name: "twitter:description", content: "Explore every space we transform with high-performance epoxy and resinous flooring systems." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/usage" }],
  }),
  component: UsagePage,
});

const icons = { residential: Home, commercial: Building2, industrial: Factory } as const;

function UsagePage() {
  return (
    <>
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Usage</span>
          </nav>
          <Reveal>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              Where our floors <span className="bg-gradient-gold bg-clip-text text-transparent">work hardest.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">
              From single-car garages to 100,000 sq ft manufacturing plants, our epoxy systems are engineered for the space, and the people, they serve.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          {usageCategories.map((cat, idx) => {
            const Icon = icons[cat.slug as keyof typeof icons];
            return (
              <Reveal key={cat.slug} delay={idx * 100}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: guideSlugByCategory[cat.slug] }}
                  className="group relative block h-full rounded-3xl border border-border bg-card p-8 shadow-soft hover:shadow-elegant transition-all overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${cat.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-gold flex items-center justify-center shadow-gold mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-gold-foreground" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{cat.tagline}</div>
                    <h2 className="font-display text-3xl font-bold mb-4">{cat.name}</h2>
                    <p className="text-muted-foreground mb-6 leading-relaxed">{cat.intro}</p>
                    <ul className="flex flex-wrap gap-2 mb-8">
                      {cat.items.slice(0, 6).map((i) => (
                        <li key={i.slug} className="text-xs px-3 py-1 rounded-full bg-muted text-foreground/80">
                          {i.name}
                        </li>
                      ))}
                      {cat.items.length > 6 && (
                        <li className="text-xs px-3 py-1 rounded-full bg-muted text-foreground/80">
                          +{cat.items.length - 6} more
                        </li>
                      )}
                    </ul>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:gap-3 transition-all">
                      Read the {cat.name.toLowerCase()} guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border bg-muted/20">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Knowledge Base</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                  All <span className="bg-gradient-gold bg-clip-text text-transparent">guides.</span>
                </h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:gap-3 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post, idx) => (
              <Reveal key={post.slug} delay={idx * 80}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: post.slug }}
                  className="group relative block h-full rounded-3xl border border-border bg-card p-8 shadow-soft hover:shadow-elegant transition-all overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${accent[post.category]} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{post.heroTagline}</div>
                    <h3 className="font-display text-2xl font-bold mb-4 leading-tight">{post.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="w-3.5 h-3.5" /> {post.readTime}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
